import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "@/lib/api";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from "recharts";
import RegionRevenueDrilldown from "./RegionRevenueDrilldown"; // <-- NEW

const fmtMK = (n: number) =>
  new Intl.NumberFormat("en-MW", {
    style: "currency",
    currency: "MWK",
    maximumFractionDigits: 0,
  }).format(n);

interface CarRef {
  _id: string;
  name?: string;
}
interface Booking {
  _id: string;
  car: string | CarRef;
  customer: string;
  dateBooked: string;
  returnDate: string;
  status: "Pending" | "Approved" | "Rejected" | "Posted";
}
interface TopCar {
  carId: string;
  name: string;
  times: number;
}
interface UtilItem {
  model: string;
  total: number;
  booked: number;
}
interface RevenueToday {
  startAt: string;
  endAt: string;
  byRegion: {
    bt?: number; // Southern (legacy key)
    eastern?: number;
    central?: number;
    northern?: number;
  };
  total: number;
}

interface RevHistItem {
  date: string;
  revenue: number;
  count: number;
}
interface BookedNow {
  byRegion: { bt: number; eastern: number; central: number; northern: number };
  total: number;
}
type ApiResponse<T> = { status: number; data: T };

const COLORS = {
  blue: "#1e3a8a",
  yellow: "#f59e0b",
  teal: "#0d9488",
  indigo: "#4f46e5",
  slate: "#64748b",
};
type RangeKey =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth";

function startOfTodaySix() {
  const now = new Date();
  const d = new Date(now);
  d.setHours(6, 0, 0, 0);
  if (now < d) d.setDate(d.getDate() - 1);
  return d;
}
function toISO(d: Date) {
  return new Date(d).toISOString();
}

function computeRange(key: RangeKey): {
  start?: string;
  end?: string;
  days?: number;
  label: string;
} {
  const now = new Date();
  const today6 = startOfTodaySix();

  if (key === "today") {
    return { start: toISO(today6), end: toISO(now), label: "Today" };
  }
  if (key === "yesterday") {
    const start = new Date(today6);
    start.setDate(start.getDate() - 1);
    const end = new Date(today6);
    end.setMilliseconds(end.getMilliseconds() - 1);
    return { start: toISO(start), end: toISO(end), label: "Yesterday" };
  }
  if (key === "last7") {
    const start = new Date(today6);
    start.setDate(start.getDate() - 6);
    return { start: toISO(start), end: toISO(now), label: "Last 7 Days" };
  }
  if (key === "last30") {
    const start = new Date(today6);
    start.setDate(start.getDate() - 29);
    return { start: toISO(start), end: toISO(now), label: "Last 30 Days" };
  }
  if (key === "thisMonth") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 6, 0, 0, 0);
    return { start: toISO(start), end: toISO(now), label: "This Month" };
  }
  if (key === "lastMonth") {
    const start = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1,
      6,
      0,
      0,
      0
    );
    const end = new Date(now.getFullYear(), now.getMonth(), 1, 6, 0, 0, 0);
    end.setMilliseconds(end.getMilliseconds() - 1);
    return { start: toISO(start), end: toISO(end), label: "Last Month" };
  }
  return { start: toISO(today6), end: toISO(now), label: "Today" };
}

// format YYYY-MM-DD
function ymd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// fill missing days with zeros between start..end (inclusive)
function fillSeriesWithZeros(
  startISO: string,
  endISO: string,
  rows: RevHistItem[]
): RevHistItem[] {
  const map = new Map(rows.map((r) => [r.date, r]));
  const start = new Date(startISO);
  const end = new Date(endISO);

  const out: RevHistItem[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = ymd(d);
    const hit = map.get(key);
    out.push({
      date: key,
      revenue: hit ? hit.revenue : 0,
      count: hit ? hit.count : 0,
    });
  }
  return out;
}

const DashboardPage = () => {
  const [stats, setStats] = useState([
    { label: "Total Cars", value: 0 },
    { label: "Booked", value: 0 },
    { label: "Available", value: 0 },
    { label: "Pending Bookings", value: 0 },
  ]);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [revToday, setRevToday] = useState<RevenueToday | null>(null);
  const [util, setUtil] = useState<UtilItem[]>([]);
  const [revHist, setRevHist] = useState<RevHistItem[]>([]);
  const [topCars, setTopCars] = useState<TopCar[]>([]);
  const [loading, setLoading] = useState(false);
  const [rangeKey, setRangeKey] = useState<RangeKey>("last30");
  const [bookedNow, setBookedNow] = useState<BookedNow | null>(null);

  const fetchAnalyticsForRange = async (key: RangeKey) => {
    const r = computeRange(key);
    setLoading(true);
    try {
      const params = { start: r.start!, end: r.end! };
      const [rt, mu, rh, tc, bn] = await Promise.all([
        axios.get<ApiResponse<RevenueToday>>("/dashboard/revenue-today", {
          params,
        }),
        axios.get<ApiResponse<UtilItem[]>>("/dashboard/model-utilization"),
        axios.get<ApiResponse<RevHistItem[]>>("/dashboard/revenue-history", {
          params,
        }),
        axios.get<ApiResponse<TopCar[]>>("/dashboard/top-cars", {
          params: { ...params, limit: 5 },
        }),
        axios.get<
          ApiResponse<{ byRegion: BookedNow["byRegion"]; total: number }>
        >("/dashboard/booked-now-by-region"),
      ]);

      setRevToday(rt.data.data);
      setUtil(mu.data.data);

      const raw = Array.isArray(rh.data.data) ? rh.data.data : [];
      const filled = fillSeriesWithZeros(r.start!, r.end!, raw);
      filled.sort((a, b) => a.date.localeCompare(b.date));
      setRevHist(filled);

      setTopCars(tc.data.data);
      setBookedNow(bn.data.data);
    } catch (err) {
      toast.error("Failed to load analytics for selected range");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get("/dashboard/stats");
        const payload = res?.data?.data ?? res?.data ?? {};
        const totalCars = Number(payload.totalCars ?? 0);
        const booked = Number(payload.booked ?? 0);
        const available = Number(payload.available ?? 0);
        const pending = Number(payload.pendingBookings ?? 0);
        const recents: Booking[] = Array.isArray(payload.recentBookings)
          ? payload.recentBookings
          : [];

        setStats([
          { label: "Total Cars", value: totalCars },
          { label: "Booked", value: booked },
          { label: "Available", value: available },
          { label: "Pending Bookings", value: pending },
        ]);

        setRecentBookings(recents);
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Failed to load dashboard stats";
        toast.error(msg);
        console.error(err);
      }
    };

    fetchStats();
    fetchAnalyticsForRange(rangeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmAndDelete = async (bookingId: string) => {
    try {
      await axios.delete(`/bookings/${bookingId}`);
      toast.success("Booking deleted");
      setRecentBookings((prev) => prev.filter((b) => b._id !== bookingId));
    } catch (err) {
      toast.error("Failed to delete booking");
      console.error(err);
    } finally {
      setConfirmDeleteId(null);
    }
  };

  // NOTE: label 'bt' as Southern for display
  const pieData = revToday
    ? [
        { name: "Southern", value: revToday.byRegion.bt || 0, key: "bt" },
        {
          name: "Eastern",
          value: revToday.byRegion.eastern || 0,
          key: "eastern",
        },
        {
          name: "Central",
          value: revToday.byRegion.central || 0,
          key: "central",
        },
        {
          name: "Northern",
          value: revToday.byRegion.northern || 0,
          key: "northern",
        },
      ]
    : [];

  const pieColors = [COLORS.blue, COLORS.yellow, COLORS.teal, COLORS.indigo];

  const SmallTooltip = ({
    active,
    payload,
    label,
    radius = 8,
    fontSize = 11,
    padding = "6px 8px",
  }: {
    active?: boolean;
    payload?: Array<any>;
    label?: string;
    radius?: number;
    fontSize?: number;
    padding?: string;
  }) => {
    if (!active || !payload?.length) return null;

    const fmt = (v: any) => (typeof v === "number" ? fmtMK(v) : String(v));

    return (
      <div
        style={{
          borderRadius: radius,
          background: "rgba(255,255,255,0.98)",
          border: "1px solid #E5E7EB",
          boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
          padding,
          fontSize,
          lineHeight: 1.15,
          whiteSpace: "nowrap",
        }}
      >
        {label ? (
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        ) : null}

        {payload.map((p, i) => (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: p.color || p.payload?.fill,
                display: "inline-block",
              }}
            />
            <span style={{ fontWeight: 500 }}>
              {p.name || p.payload?.name || ""}
            </span>
            <span style={{ marginLeft: 6, fontWeight: 700 }}>
              {fmt(p.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const Pill = ({
    label,
    count,
    color,
  }: {
    label: string;
    count: number;
    color: string;
  }) => (
    <span
      className="px-2.5 py-1 rounded-full border text-xs inline-flex items-center gap-1"
      style={{ borderColor: color, color }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}: <strong className="ml-0.5">{count}</strong>
    </span>
  );

  return (
    <div className="">
      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-blue-900 text-white rounded-lg p-4 shadow hover:scale-[1.02] transition"
          >
            <p className="text-sm text-blue-200">{stat.label}</p>
            <p className="text-2xl font-bold text-yellow-400">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-3 mt-8">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["today", "Today"],
              ["yesterday", "Yesterday"],
              ["last7", "Last 7"],
              ["last30", "Last 30"],
              ["thisMonth", "This Month"],
              ["lastMonth", "Last Month"],
            ] as [RangeKey, string][]
          ).map(([key, label]) => {
            const active = rangeKey === key;
            return (
              <button
                key={key}
                disabled={loading}
                onClick={() => {
                  setRangeKey(key);
                  fetchAnalyticsForRange(key);
                }}
                className={
                  "px-3 py-1.5 text-xs rounded-full border transition " +
                  (loading ? "opacity-60 cursor-not-allowed " : "") +
                  (active
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-blue-900 border-blue-200 hover:bg-blue-50")
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {loading && (
        <div className="mt-2 text-sm text-gray-500">Loading analytics…</div>
      )}

      {/* ===== Middle: Charts row ===== */}
      <div className="grid grid-cols-1 gap-6 mt-8 lg:grid-cols-2">
        {/* Pie: Revenue by Region (06:00 window) */}
        <div className="p-4 bg-white rounded shadow">
          <h3 className="mb-2 text-lg font-semibold text-blue-900">
            Revenue by Region ({computeRange(rangeKey).label})
          </h3>

          <p className="mb-4 text-xs text-gray-500">
            {revToday ? (
              <>
                Window:&nbsp;
                <span className="font-medium">
                  {new Date(revToday.startAt).toLocaleString()} →{" "}
                  {new Date(revToday.endAt).toLocaleString()}
                </span>
                &nbsp;• Total:&nbsp;
                <span className="font-semibold">
                  MWK {revToday.total.toLocaleString("en-MW")}
                </span>
              </>
            ) : (
              "Window: —"
            )}
          </p>

          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(value: any) => fmtMK(Number(value))}
                  content={<SmallTooltip />}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-sm font-semibold text-center text-blue-900">
            Total = {revToday ? fmtMK(revToday.total) : fmtMK(0)}
          </div>
        </div>

        {/* Bar: Model Utilization */}
        <div className="p-4 bg-white rounded shadow">
          <h3 className="mb-2 text-lg font-semibold text-blue-900">
            Model Utilization (Now)
          </h3>
          <p className="mb-4 text-xs text-gray-500">
            Total vs Currently Booked
          </p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={util}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="model" />
                <YAxis />
                <Legend />
                <RTooltip
                  cursor={{ fill: "transparent", stroke: "transparent" }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div
                        style={{
                          borderRadius: 6,
                          padding: "6px 10px",
                          border: "1px solid #E5E7EB",
                          background: "rgba(255,255,255,0.98)",
                          fontSize: 11,
                          lineHeight: 1.15,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label ? (
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            {label}
                          </div>
                        ) : null}

                        {payload.map((p, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: 9999,
                                background: p.color || p.payload?.fill,
                                display: "inline-block",
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>{p.name}</span>
                            <span style={{ marginLeft: 6, fontWeight: 700 }}>
                              {p.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Bar dataKey="total" name="Total Cars" fill={COLORS.slate} />
                <Bar dataKey="booked" name="Booked Now" fill={COLORS.yellow} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {bookedNow && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Pill
                label="Southern"
                count={bookedNow.byRegion.bt}
                color={COLORS.blue}
              />
              <Pill
                label="Eastern"
                count={bookedNow.byRegion.eastern}
                color={COLORS.yellow}
              />
              <Pill
                label="Central"
                count={bookedNow.byRegion.central}
                color={COLORS.teal}
              />
              <Pill
                label="Northern"
                count={bookedNow.byRegion.northern}
                color={COLORS.indigo}
              />
              <span className="ml-auto text-xs font-semibold text-blue-900">
                Total: {bookedNow.total}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* NEW: Interactive drill-down (Region -> District) */}
      <div className="mt-8">
        <RegionRevenueDrilldown />
      </div>

      <div className="grid grid-cols-1 gap-6 mt-8 lg:grid-cols-2">
        <div className="p-4 bg-white rounded shadow">
          <div className="flex items-center justify-between">
            <h3 className="mb-2 text-lg font-semibold text-blue-900">
              Revenue ({computeRange(rangeKey).label})
            </h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revHist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  minTickGap={20}
                  tickCount={6}
                  tickFormatter={(d) =>
                    new Date(d).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })
                  }
                />
                <YAxis />
                <RTooltip
                  formatter={(v: any) => fmtMK(Number(v))}
                  content={<SmallTooltip />}
                  cursor={false}
                />
                <Line
                  type="linear"
                  dataKey="revenue"
                  name="Revenue"
                  stroke={COLORS.blue}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-4 bg-white rounded shadow">
          <h3 className="mb-2 text-lg font-semibold text-blue-900">
            Top Booked Cars ({computeRange(rangeKey).label})
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-blue-900 uppercase border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left">Car</th>
                  <th className="px-3 py-2 text-left">Bookings</th>
                </tr>
              </thead>
              <tbody>
                {topCars.map((c) => (
                  <tr key={c.carId} className="border-b border-gray-100">
                    <td className="px-3 py-2">{c.name}</td>
                    <td className="px-3 py-2 font-semibold">{c.times}</td>
                  </tr>
                ))}
                {topCars.length === 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-3 py-6 text-center text-gray-400"
                    >
                      No data yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ===== Recent Bookings (moved to bottom) ===== */}
      <div className="mt-10">
        <h2 className="mb-4 text-lg font-bold text-blue-900">
          Recent Bookings
        </h2>

        <div className="p-4 overflow-x-auto bg-white rounded shadow">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-blue-900 uppercase border-b border-gray-200">
              <tr>
                <th className="px-4 py-2">Car</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Booked</th>
                <th className="px-4 py-2">Return</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.map((b) => (
                <tr key={b._id} className="border-b border-gray-100">
                  <td className="px-4 py-2">
                    {typeof b.car === "object" && b.car
                      ? b.car.name ?? b.car._id
                      : typeof b.car === "string"
                      ? b.car
                      : "Unknown"}
                  </td>
                  <td className="px-4 py-2">{b.customer}</td>
                  <td className="px-4 py-2">
                    {new Date(b.dateBooked).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    {new Date(b.returnDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        b.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : b.status === "Pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : b.status === "Rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {confirmDeleteId === b._id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => confirmAndDelete(b._id)}
                          className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-500"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-3 py-1 text-xs text-gray-600 border rounded hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          className="text-xs font-medium text-blue-900 hover:underline"
                          onClick={() =>
                            toast.success(`Viewing booking for ${b.customer}`)
                          }
                        >
                          View
                        </button>
                        <button
                          className="text-xs font-medium text-red-600 hover:underline"
                          onClick={() => setConfirmDeleteId(b._id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {recentBookings.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-sm text-center text-gray-400"
                  >
                    No recent bookings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
